import {
  isRecoverableNuxtAssetLink,
  isRecoverableNuxtAssetUrl
} from '~/utils/nuxtAssetRecovery'

const CHUNK_ERROR_RELOAD_KEY = 'nuxt_chunk_error_recovery'
const CHUNK_ERROR_RELOAD_WINDOW_MS = 30_000
const MAX_CHUNK_ERROR_RELOADS = 2

type ChunkErrorRecoveryState = {
  attempts: number
  startedAt: number
}

const isChunkLoadError = (errorLike: unknown) => {
  const msg = errorLike instanceof Error
    ? errorLike.message
    : typeof errorLike === 'string'
      ? errorLike
      : String(errorLike || '')

  return msg.includes('Failed to fetch dynamically imported') ||
    msg.includes('Loading chunk') ||
    msg.includes('Loading CSS chunk') ||
    msg.includes('Importing a module script failed')
}

const readRecoveryState = (): ChunkErrorRecoveryState | null => {
  try {
    const raw = sessionStorage.getItem(CHUNK_ERROR_RELOAD_KEY)
    if (!raw) return null
    const state = JSON.parse(raw) as Partial<ChunkErrorRecoveryState>
    if (
      typeof state.attempts !== 'number' ||
      typeof state.startedAt !== 'number'
    ) {
      return null
    }
    return {
      attempts: state.attempts,
      startedAt: state.startedAt
    }
  } catch {
    return null
  }
}

const writeRecoveryState = (state: ChunkErrorRecoveryState) => {
  try {
    sessionStorage.setItem(CHUNK_ERROR_RELOAD_KEY, JSON.stringify(state))
  } catch {
    // Storage failures should not block chunk recovery.
  }
}

const clearRecoveryState = () => {
  try {
    sessionStorage.removeItem(CHUNK_ERROR_RELOAD_KEY)
  } catch {
    // Ignore storage failures.
  }
}

const clearRecoveryUrlParam = () => {
  if (typeof window === 'undefined') return

  const currentUrl = new URL(window.location.href)
  if (!currentUrl.searchParams.has('__kendrick_reload')) return

  currentUrl.searchParams.delete('__kendrick_reload')
  window.history.replaceState(window.history.state, '', currentUrl.toString())
}

const clearBrowserCaches = async () => {
  if (typeof window === 'undefined') return

  try {
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations()
      await Promise.all(registrations.map(registration => registration.unregister()))
    }
  } catch (error) {
    console.warn('[Kendrick] Service worker cleanup failed:', error)
  }

  try {
    if ('caches' in window) {
      const keys = await caches.keys()
      await Promise.all(
        keys
          .filter(key => key.startsWith('kendrick-'))
          .map(key => caches.delete(key))
      )
    }
  } catch (error) {
    console.warn('[Kendrick] Cache cleanup failed:', error)
  }
}

const recoverFromChunkError = () => {
  if (typeof window === 'undefined') return false

  const now = Date.now()
  const currentState = readRecoveryState()
  const state = currentState && now - currentState.startedAt < CHUNK_ERROR_RELOAD_WINDOW_MS
    ? currentState
    : { attempts: 0, startedAt: now }

  if (state.attempts >= MAX_CHUNK_ERROR_RELOADS) {
    return false
  }

  const nextState = {
    attempts: state.attempts + 1,
    startedAt: state.startedAt
  }
  writeRecoveryState(nextState)

  void clearBrowserCaches().finally(() => {
    const nextUrl = new URL(window.location.href)
    nextUrl.searchParams.set('__kendrick_reload', String(now))
    window.location.replace(nextUrl.toString())
  })

  return true
}

const getRecoverableNuxtResourceUrl = (event: Event) => {
  const target = event.target
  const currentOrigin = window.location.origin

  if (target instanceof HTMLScriptElement) {
    return isRecoverableNuxtAssetUrl(target.src, currentOrigin) ? target.src : ''
  }

  if (target instanceof HTMLLinkElement) {
    return isRecoverableNuxtAssetLink(target.rel, target.as, target.href, currentOrigin) ? target.href : ''
  }

  return ''
}

export default defineNuxtPlugin((nuxtApp) => {
  nuxtApp.hook('app:mounted', () => {
    window.setTimeout(clearRecoveryUrlParam, 1000)
    window.setTimeout(clearRecoveryState, CHUNK_ERROR_RELOAD_WINDOW_MS)
  })

  // Recover from dynamic import/chunk load failures after deploys.
  nuxtApp.hook('app:chunkError', ({ error }) => {
    if (isChunkLoadError(error)) {
      recoverFromChunkError()
    }
  })

  if (process.env.NODE_ENV === 'production') {
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      if (isChunkLoadError(event.reason) && recoverFromChunkError()) {
        event.preventDefault()
      }
    }

    const handleWindowError = (event: ErrorEvent) => {
      if (isChunkLoadError(event.error || event.message) && recoverFromChunkError()) {
        event.preventDefault()
      }
    }

    const handleResourceError = (event: Event) => {
      if (!getRecoverableNuxtResourceUrl(event)) return
      recoverFromChunkError()
    }

    window.addEventListener('unhandledrejection', handleUnhandledRejection)
    window.addEventListener('error', handleWindowError)
    window.addEventListener('error', handleResourceError, true)

    nuxtApp.hook('app:unmounted', () => {
      window.removeEventListener('unhandledrejection', handleUnhandledRejection)
      window.removeEventListener('error', handleWindowError)
      window.removeEventListener('error', handleResourceError, true)
    })
  }
})
